import React, { useState, useEffect } from 'react';
import { 
  countTotalNumOfPunchlines, 
  countTotalNumOfTaglines, 
  countTotalSetupLengthOfPerformance, 
  countTotalPunchlineLengthOfPerformance,
  countTotalTaglineLengthOfPerformance
} from './JsonTextAnalysis';
import CurrentGraph from './currentGraph';
import './bitAnalysisComponent.css';
import LoadingBarPercentageSetupPunchLaughs from './LoadingBarPercentageSetupPunchLaughs';
import Tooltip from '@mui/material/Tooltip';

const BitAnalysisComponent = ({ bit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [totalPunchlines, setTotalPunchlines] = useState(0);
  const [totalTaglines, setTotalTaglines] = useState(0);
  const [totalSetupLength, setTotalSetupLength] = useState(0);
  const [totalPunchlineLength, setTotalPunchlineLength] = useState(0);
  const [totalTaglineLength, setTotalTaglineLength] = useState(0); 
  const [totalWords, setTotalWords] = useState(0);
  const [numOfLaughs, setNumOfLaughs] = useState(0);
  const [biggestLaugh, setBiggestLaugh] = useState(0);
  const [formattedTotalTime, setFormattedTotalTime] = useState('00m 00s');
  const [totalTime, setTotalTime] = useState(0);
  const [totalTimeInSetups, setTotalTimeInSetups] = useState(0);
  const [totalTimeLaughing, setTotalTimeLaughing] = useState(0);
  const [speakingRate, setSpeakingRate] = useState(0);
  const [laughScore, setLaughScore] = useState(0);
  const [laughQualityScore, setLaughQualityScore] = useState('0%');
  const [audienceResponseArray, setAudienceResponseArray] = useState([]);

  useEffect(() => {  
    if (!bit || !Array.isArray(bit) || bit.length === 0) {
      return;
    }  
    
    const punchlines = countTotalNumOfPunchlines(bit);
    const taglines = countTotalNumOfTaglines(bit);
  
    const setupLength = countTotalSetupLengthOfPerformance(bit);
    const punchlineLength = countTotalPunchlineLengthOfPerformance(bit);
    const taglineLength = countTotalTaglineLengthOfPerformance(bit);
  
    const words = setupLength + punchlineLength + taglineLength;
    setTotalWords(words);
  
    const audienceResponseArray = bit.flatMap(({ punchline = {}, taglines = [] }) => { 
      const punchlineResponses = punchline.response || [];
      const taglineResponses = taglines.flatMap(tagline => tagline.punchline?.response || []);
      return [...punchlineResponses, ...taglineResponses];
    });
  
    setAudienceResponseArray(audienceResponseArray);
  
    const totalTimeBeforeLaugh = audienceResponseArray.filter(response => response === 0).length;
    const totalTimeLaughing = audienceResponseArray.filter(response => response !== 0).length;
    const totalTime = totalTimeBeforeLaugh + totalTimeLaughing;
    const totalLaughScore = audienceResponseArray.reduce((sum, value) => sum + value, 0);
    const maxScore = audienceResponseArray.length * 10 || 1; // Prevent division by zero
    const calculatedLaughQualityScore = ((totalLaughScore / maxScore) * 100).toFixed(1);
    const speakingRate = totalTimeBeforeLaugh > 0 ? (words / totalTimeBeforeLaugh) * 60 : 0;
    const formattedTime = totalTime ? `${Math.floor(totalTime / 60).toString().padStart(2, '0')}m ${totalTime % 60}s` : '00m 00s';
  
    setBiggestLaugh(audienceResponseArray.length > 0 ? Math.max(...audienceResponseArray) : 0);
    setLaughScore(totalLaughScore);
    setLaughQualityScore(`${calculatedLaughQualityScore}%`);
    setTotalPunchlines(punchlines);
    setTotalTaglines(taglines);
    setNumOfLaughs(punchlines + taglines);
    setTotalSetupLength(setupLength);
    setTotalPunchlineLength(punchlineLength);
    setTotalTaglineLength(taglineLength);
    setTotalTime(totalTime);
    setTotalTimeInSetups(totalTimeBeforeLaugh);
    setTotalTimeLaughing(totalTimeLaughing);
    setSpeakingRate(speakingRate.toFixed(1));
    setFormattedTotalTime(formattedTime);
  
  }, [bit]);
  
  const setupPercentage = totalWords > 0 ? ((totalSetupLength / totalWords) * 100).toFixed(0) : 0;
  const punchlinePercentage = totalWords > 0 ? ((totalPunchlineLength / totalWords) * 100).toFixed(0) : 0;
  const taglinePercentage = totalWords > 0 ? ((totalTaglineLength / totalWords) * 100).toFixed(0) : 0;
  const percentageOfLaughterInPerformance = totalTime > 0 ? ((totalTimeLaughing / totalTime) * 100).toFixed(1) : 0;

  return (
    <div className={`bit-analysis-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <button className="collapse-button" onClick={() => setIsExpanded(!isExpanded)}>
        {isExpanded ? 'Hide' : 'View Bit Analysis'}
      </button>

      {isExpanded && (
        <div className="analysis-content">
          <div className="performance-analysis-top">
            <div className="performance-analysis-words">
              <Tooltip title="Total number of words in the bit">
                <p>Total Words: <strong>{totalWords}</strong></p>
              </Tooltip>

              <Tooltip title="Total number of words in the setups of the bit">
                <p>Setups: <strong>{totalSetupLength}</strong> words - <strong>{setupPercentage}%</strong></p>
              </Tooltip>
              <Tooltip title="Total number of words in the punchlines of the bit">
                <p>Punchlines: <strong>{totalPunchlineLength}</strong> words - <strong>{punchlinePercentage}%</strong></p>
              </Tooltip>
              <Tooltip title="Total number of words in the taglines of the bit">
                <p>Taglines: <strong>{totalTaglineLength}</strong> words - <strong>{taglinePercentage}%</strong></p>
              </Tooltip>
              <Tooltip title={`Laughs: ${numOfLaughs} (Punchlines: ${totalPunchlines}, Taglines: ${totalTaglines})`}>
                <p>Laughs: <strong>{numOfLaughs}</strong></p>
              </Tooltip>
              <Tooltip title={`Biggest laugh: ${biggestLaugh}`}>
                <p>Biggest Laugh: <strong>{biggestLaugh}</strong></p>
              </Tooltip>

              <Tooltip title="The overall laugh score for the bit">
                <p>Laugh Score: <strong>{laughScore}</strong></p>
              </Tooltip>
              <Tooltip title="Laugh quality score percentage">
                <p>Laugh Quality Score: <strong>{laughQualityScore}</strong></p>
              </Tooltip>
            </div>

            <div className="performance-analysis-time">
              <Tooltip title="Total time the bit lasted">
                <p>Total Time: <strong>{formattedTotalTime}</strong></p>
              </Tooltip>
              <Tooltip title="Time spent with the comedian talking">
                <p>Comedian Talking: {totalTimeInSetups} sec</p>
              </Tooltip>
              <Tooltip title="Time spent with the audience laughing">
                <p>Laughter: {totalTimeLaughing} sec | <strong>{percentageOfLaughterInPerformance}%</strong></p>
              </Tooltip>
              <Tooltip title="Speaking rate (words per minute)">
                <p>Speaking Rate: <strong>{speakingRate} WPM</strong></p>
              </Tooltip>
            </div>
          </div>

          <div className="mini-graph-area">
            <LoadingBarPercentageSetupPunchLaughs 
              setupPercentage={setupPercentage} 
              punchlinePercentage={punchlinePercentage} 
              taglinePercentage={taglinePercentage} 
              laughPercentage={percentageOfLaughterInPerformance} 
            />
            <div className="mini-graph-container">
              {audienceResponseArray.length > 0 ? (
                <CurrentGraph data={audienceResponseArray} />
              ) : (
                <p>No audience response data available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BitAnalysisComponent;
